import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCheckCircle, FiClock } from 'react-icons/fi';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import simuladoIcon from '@/assets/images/infantil/simulado.png';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

// Card lúdico de prova (aluno + tema infantil). Espelha o PlayfulMockExamsCard
// (mesma ilustração de simulado, provisória até termos uma arte própria de
// "prova"). A variante corporativa (admin / tema padrão) continua em
// exams-resolution-card.jsx.
export function PlayfulExamsResolutionCard({ exam, index = 0 }) {
  const { t } = useTranslation();

  const isExpired = moment().isAfter(moment(exam?.['finished-at']));

  const badge = isExpired
    ? { text: 'Prazo encerrado', variant: 'muted' }
    : {
        approved: { text: t('exams.statusInApproved'), variant: 'success-soft' },
        review: { text: t('exams.statusInAnalysis'), variant: 'info-soft' },
        disapproved: { text: t('exams.statusInDisapproved'), variant: 'danger-soft' },
        accepting_answers: { text: 'Aceitando respostas', variant: 'amber-soft' }
      }[exam.status] || { text: t('exams.statusUnrealized'), variant: 'muted' };

  const questionCount = exam?.['question-count'] ?? 0;
  const taxonomies = exam?.taxonomies || [];

  return (
    <article
      style={{ animation: `trilha-in 380ms ${EASE_OUT} ${index * 70}ms both` }}
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-[24px] border border-neutral-100 bg-[var(--color-bg-cream)] p-6',
        'shadow-card transition-[box-shadow,transform,border-color] duration-150 ease-out-strong hover:-translate-y-0.5 hover:shadow-card-hover'
      )}
    >
      {/* Faixa de céu suave no topo (teal, combinando com o ícone). */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-16 rounded-t-[24px]"
        style={{ background: 'linear-gradient(180deg, rgba(214,253,255,0.5) 0%, transparent 100%)' }}
      />

      {/* Decoração sutil no canto. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-2 right-3 text-2xl opacity-25"
      >
        📄
      </span>

      {/* Topo: ilustração + badge de status. */}
      <div className="relative mb-5 flex items-start justify-between">
        <img
          src={simuladoIcon}
          alt=""
          aria-hidden="true"
          className="h-20 w-20 object-contain drop-shadow-sm"
        />
        <Badge
          variant={badge.variant}
          className="rounded-full px-2.5 py-1 text-xs font-bold"
        >
          {badge.text}
        </Badge>
      </div>

      <h3
        className="text-lg font-bold"
        style={{ color: '#222731' }}
      >
        {exam?.title}
      </h3>

      {/* Matérias como pílulas. */}
      {taxonomies.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {taxonomies.slice(0, 2).map((tax, i) => (
            <Badge
              key={i}
              variant="info-soft"
              className="rounded-full px-2.5 py-0.5 text-xs font-semibold text-[var(--color-primary)]"
            >
              {tax?.name || tax}
            </Badge>
          ))}
          {taxonomies.length > 2 && (
            <Badge
              variant="muted"
              className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
            >
              +{taxonomies.length - 2}
            </Badge>
          )}
        </div>
      )}

      {/* Meta: nº de questões + data de publicação, igual ao card de simulado. */}
      <div className="mb-6 mt-4 flex flex-1 items-center gap-4 text-sm text-neutral-500">
        <span className="flex items-center gap-1.5 font-semibold">
          <FiCheckCircle className="h-4 w-4 text-[var(--color-mission-medium)]" />
          {questionCount} {questionCount === 1 ? 'questão' : 'questões'}
        </span>
        {exam?.['published-at'] && (
          <span className="flex items-center gap-1.5">
            <FiClock className="h-4 w-4" />
            {moment(exam['published-at']).format('DD/MM/YYYY')}
          </span>
        )}
      </div>

      {/* CTA: só bloqueia quando o prazo já passou. Dentro do prazo, o
      acesso é sempre liberado — a tela "Sobre" decide se é para iniciar,
      refazer ou ver resultados (mesma regra do tema default). */}
      {isExpired ? (
        <button
          type="button"
          disabled
          className="w-full cursor-not-allowed rounded-full border border-neutral-200 bg-neutral-100 py-3 text-sm font-bold text-neutral-400"
        >
          {badge.text}
        </button>
      ) : (
        <Link
          to={`/avaliacoes/sobre/${exam?.id}`}
          className="group/btn relative block w-full overflow-hidden rounded-full py-3 text-center text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
          style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 4px 0 var(--color-mission-dark), 0 6px 12px rgba(245,123,46,0.35)' }}
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
            style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, transparent 100%)' }}
          />
          <span className="relative inline-flex items-center justify-center gap-2">
            {t('exams.accessExam')}
            <FiArrowRight className="h-4 w-4 transition-transform duration-150 group-hover/btn:translate-x-0.5" />
          </span>
        </Link>
      )}
    </article>
  );
}
